import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { date, object } from "yup";
import { FormikProps } from "formik/dist/types";
import { Form, Formik, FormikConfig } from "formik";
import { endOfMonth, format, formatISO, subMonths } from "date-fns";
import classNames from "classnames";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import { useCanLockEntity } from "@hooks/permissions/useCanLockEntity";
import { updateLock } from "../../../lib/lock";
import { DatePickerInput } from "../../forms/dates/DatePickerInput/DatePickerInput";
import { Button } from "../../general/Button/Button";
import { LockPolicy } from "../../../common/types/lock";
import { Entity } from "../../../common/types/entity";
import { EntityDisplay } from "../../entity/EntityDisplay";
import { LockIcon, UnlockIcon } from "../../../icons";
import { useLockDate } from "../../../api/lock.api";
import {
    getUTCDateForPST,
    monthlyDateFormat,
} from "../../../common/helpers/date";
import styles from "./WorkspaceLock.module.scss";

interface LockForm {
    date: string | null;
    entityId: number;
}

interface EntityLockFormProps {
    entity: Entity;
}

export const EntityLockForm: React.FC<EntityLockFormProps> = ({ entity }) => {
    const lockDate = useLockDate(entity.id);
    const isAdmin = useCanLockEntity();

    const formRef = useRef<FormikProps<LockForm>>();

    const initialValues = useMemo(() => {
        if (!lockDate) {
            return {
                date: null,
                entityId: entity.id,
            };
        }

        return {
            date: lockDate ? String(lockDate) : null,
            entityId: entity.id,
        };
    }, [lockDate, entity.id]);

    useEffect(() => {
        formRef.current?.resetForm({
            values: initialValues,
        });
    }, [lockDate, initialValues]);

    const form: FormikConfig<LockForm> = {
        initialValues,
        validationSchema: object().shape({
            date: date().nullable(),
        }),
        onSubmit: async (value) => {
            const { date: newLockDateValue } = value;
            const newLockDate = newLockDateValue
                ? endOfMonth(getUTCDateForPST(new Date(newLockDateValue)))
                : null;

            await updateLock({
                entityId: entity.id,
                date: newLockDate
                    ? formatISO(newLockDate, {
                          representation: "date",
                      })
                    : null,
                policy: LockPolicy.ALL,
            });

            const toastMessage = newLockDate
                ? `Entity is locked up to ${format(newLockDate, monthlyDateFormat)}`
                : "Entity is unlocked";

            toast(toastMessage);
        },
    };

    const tileDisabled = useCallback(
        ({ date: chosenDate }: { date: Date }) =>
            chosenDate > endOfMonth(subMonths(new Date(), 1)),
        [],
    );

    const tooltip = useMemo(() => {
        if (!initialValues.date) {
            return "Entity is unlocked";
        }

        const newLockDate = format(initialValues.date, monthlyDateFormat);
        return `Entity is locked up to ${newLockDate}`;
    }, [initialValues.date]);

    return (
        <tr>
            <td>
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip id={`change-lock-icon-${entity.id}`}>
                            {tooltip}
                        </Tooltip>
                    }
                >
                    <div
                        className={classNames(styles.statusIcon, {
                            [styles.locked]: initialValues.date,
                            [styles.unlocked]: !initialValues.date,
                        })}
                    >
                        {initialValues.date ? (
                            <LockIcon className="icon-color-current" />
                        ) : (
                            <UnlockIcon className="icon-color-current" />
                        )}
                    </div>
                </OverlayTrigger>
            </td>
            <td className={styles.entityCell}>
                <EntityDisplay entity={entity} />
            </td>
            <td className={styles.dateCell}>
                <Formik {...form} innerRef={formRef as any}>
                    {(formik) => (
                        <Form>
                            <fieldset>
                                <DatePickerInput
                                    keepOpenOnSelect
                                    monthSelect
                                    preventTyping
                                    disabled={!isAdmin}
                                    value={formik.values.date ?? ""}
                                    name={`date-${entity.id}`}
                                    inputSize="sm"
                                    placeholder="Select month"
                                    inputClassName={styles.datePickerInput}
                                    tileDisabled={tileDisabled}
                                    popoverPlacement="bottom-end"
                                    onChange={(value) => {
                                        if (value) {
                                            formik.setFieldValue("date", value);
                                        }
                                    }}
                                >
                                    {(close) => (
                                        <div className={styles.buttonContainer}>
                                            {initialValues.date ? (
                                                <>
                                                    <Button
                                                        variant="secondary"
                                                        className="w-50"
                                                        onClick={() => {
                                                            formik.setFieldValue(
                                                                "date",
                                                                null,
                                                            );
                                                            formik.submitForm();
                                                            close();
                                                        }}
                                                    >
                                                        Unlock
                                                    </Button>
                                                    <Button
                                                        className="w-50"
                                                        onClick={() => {
                                                            formik.submitForm();
                                                            close();
                                                        }}
                                                    >
                                                        Save
                                                    </Button>
                                                </>
                                            ) : (
                                                <Button
                                                    block
                                                    variant="secondary"
                                                    disabled={
                                                        !formik.values.date
                                                    }
                                                    onClick={() => {
                                                        formik.submitForm();
                                                        close();
                                                    }}
                                                >
                                                    Lock
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </DatePickerInput>
                            </fieldset>
                        </Form>
                    )}
                </Formik>
            </td>
        </tr>
    );
};
